/**
 * Username Tracking Service
 * 
 * Provides atomic username claiming and uniqueness enforcement using Firestore transactions.
 * 
 * This service maintains a separate `usernames` collection where each document ID is the username
 * string. This allows for fast O(1) lookups to check if a username is taken, and ensures
 * uniqueness through Firestore transactions.
 * 
 * Key features:
 * - Atomic username claiming (prevents race conditions)
 * - Automatic cleanup of previous usernames when changed
 * - Username normalization (lowercase, URL-safe)
 * - Clear error messages for taken usernames
 */

import { doc, getDoc, setDoc, deleteDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * Normalizes a username to be URL-safe and consistent
 * - Trims whitespace
 * - Converts to lowercase
 * - Removes leading @ symbol if present
 * - Allows only letters, numbers, underscores, and periods
 * 
 * @param {string} username - Raw username input
 * @returns {string} - Normalized username
 */
export function normalizeUsername(username) {
  if (!username || typeof username !== 'string') {
    return '';
  }
  
  return username
    .trim()
    .replace(/^@+/, '') // Remove leading @ symbols
    .toLowerCase()
    .replace(/[^a-z0-9._]/g, '') // Keep only letters, numbers, underscore, period
    .substring(0, 30); // Limit length
}

/**
 * Validates that a username meets our requirements
 * 
 * @param {string} username - Username to validate
 * @returns {{ valid: boolean, error?: string }} - Validation result
 */
export function validateUsername(username) {
  const normalized = normalizeUsername(username);
  
  if (!normalized || normalized.length === 0) {
    return { valid: false, error: 'Username is required' };
  }
  
  if (normalized.length < 3) {
    return { valid: false, error: 'Username must be at least 3 characters' };
  }
  
  if (normalized.length > 30) {
    return { valid: false, error: 'Username must be 30 characters or less' };
  }
  
  // Check for reserved usernames (add more as needed)
  const reserved = ['admin', 'api', 'www', 'mail', 'ftp', 'root', 'support', 'help', 'about', 'contact'];
  if (reserved.includes(normalized)) {
    return { valid: false, error: 'This username is reserved' };
  }
  
  // Must start with a letter or number
  if (!/^[a-z0-9]/.test(normalized)) {
    return { valid: false, error: 'Username must start with a letter or number' };
  }
  
  return { valid: true };
}

/**
 * Claims a username for a user using an atomic Firestore transaction.
 * 
 * This function:
 * 1. Normalizes the new username
 * 2. If there's a previous username, releases it (only if it still belongs to this user)
 * 3. Checks if the new username is available
 * 4. Claims the new username atomically
 * 
 * @param {Object} params - Parameters object
 * @param {string} params.userId - Firebase Auth user ID
 * @param {string} params.newUsername - New username to claim
 * @param {string} [params.previousUsername] - Previous username (if changing)
 * @param {string} [params.profileId] - Optional profile document ID (for future use)
 * @returns {Promise<void>}
 * @throws {Error} - Throws "USERNAME_TAKEN" if username is already taken by another user
 */
export async function claimUsernameForUser({
  userId,
  newUsername,
  previousUsername,
  profileId,
}) {
  if (!userId) {
    throw new Error('User ID is required');
  }
  
  if (!newUsername) {
    throw new Error('Username is required');
  }
  
  // Normalize the new username
  const normalizedNew = normalizeUsername(newUsername);
  
  // Validate the username
  const validation = validateUsername(normalizedNew);
  if (!validation.valid) {
    throw new Error(validation.error);
  }
  
  // Normalize previous username if provided
  const normalizedPrevious = previousUsername ? normalizeUsername(previousUsername) : null;
  
  // If previous and new are the same, no work needed
  if (normalizedPrevious === normalizedNew) {
    return;
  }
  
  // Use a Firestore transaction to ensure atomicity
  await runTransaction(db, async (transaction) => {
    // Step 1: If there's a previous username, check if we own it and release it
    if (normalizedPrevious && normalizedPrevious !== normalizedNew) {
      const previousUsernameRef = doc(db, 'usernames', normalizedPrevious);
      const previousUsernameDoc = await transaction.get(previousUsernameRef);
      
      if (previousUsernameDoc.exists()) {
        const previousData = previousUsernameDoc.data();
        // Only delete if we still own it (prevents race conditions)
        if (previousData.userId === userId || previousData.uid === userId) {
          transaction.delete(previousUsernameRef);
        }
      }
    }
    
    // Step 2: Check if the new username is available
    const newUsernameRef = doc(db, 'usernames', normalizedNew);
    const newUsernameDoc = await transaction.get(newUsernameRef);
    
    if (newUsernameDoc.exists()) {
      const existingData = newUsernameDoc.data();
      const ownerId = existingData.uid || existingData.userId;
      // If it exists and belongs to a different user, throw error
      if (ownerId && ownerId !== userId) {
        const error = new Error('USERNAME_TAKEN');
        error.code = 'USERNAME_TAKEN';
        throw error;
      }
      // If it exists and belongs to us, we can update it (no-op, but we'll update timestamps)
    }
    
    // Step 3: Claim the username (create or update)
    const now = serverTimestamp();
    const usernameData = {
      uid: userId,
      userId, // keep legacy field for backward compatibility
      ...(profileId && { profileId }),
      updatedAt: now,
    };
    
    // If it's a new document, also set createdAt
    if (!newUsernameDoc.exists()) {
      usernameData.createdAt = now;
    }
    
    transaction.set(newUsernameRef, usernameData, { merge: true });
  });
}

/**
 * Releases a username (removes it from the usernames collection)
 * Only releases if the username belongs to the specified user
 * 
 * @param {string} userId - Firebase Auth user ID
 * @param {string} username - Username to release
 */
export async function releaseUsername(userId, username) {
  if (!userId || !username) {
    return;
  }
  
  const normalized = normalizeUsername(username);
  if (!normalized) {
    return;
  }
  
  await runTransaction(db, async (transaction) => {
    const usernameRef = doc(db, 'usernames', normalized);
    const usernameDoc = await transaction.get(usernameRef);
    
    if (usernameDoc.exists()) {
      const data = usernameDoc.data();
      // Only delete if we own it
      if (data.userId === userId || data.uid === userId) {
        transaction.delete(usernameRef);
      }
    }
  });
}

/**
 * Checks if a username is available (not taken by any user)
 * 
 * @param {string} username - Username to check
 * @returns {Promise<boolean>} - true if available, false if taken
 */
export async function isUsernameAvailable(username) {
  const normalized = normalizeUsername(username);
  if (!normalized) {
    return false;
  }
  
  try {
    const usernameRef = doc(db, 'usernames', normalized);
    const usernameDoc = await getDoc(usernameRef);
    return !usernameDoc.exists();
  } catch (error) {
    console.error('Error checking username availability:', error);
    return false;
  }
}

/**
 * Gets the userId that owns a username
 * 
 * @param {string} username - Username to look up
 * @returns {Promise<string|null>} - User ID if found, null otherwise
 */
export async function getUsernameOwner(username) {
  const normalized = normalizeUsername(username);
  if (!normalized) {
    return null;
  }
  
  try {
    const usernameRef = doc(db, 'usernames', normalized);
    const usernameDoc = await getDoc(usernameRef);
    
    if (usernameDoc.exists()) {
      return usernameDoc.data().userId || null;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting username owner:', error);
    return null;
  }
}

